import { Component, Input, OnChanges, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { UpdatePointCustomField } from 'src/app/project/modules/points/points.actions';
import { TPoint } from 'src/app/project/modules/points/points.model';

import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { SitePointFilterService } from 'src/app/project/modules/filters/site-point-filter.service';
import { PointsUpdateService } from 'src/app/project/modules/points/points-update.service';
import { PointActivityService } from '../../point-timeline/point-activity.service';
import { PointFieldsService } from '../point-fields.service';

import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';

import { TPointUpdate } from '@project/view-models';
import { Instance } from 'flatpickr/dist/types/instance';
import { of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { getTimeAsUtcMidday } from 'src/app/core/helpers/date/date';
import { TPointCustomField } from 'src/app/project/view-models/custom-field-response-model';
import { EIconPath } from '../../../../../shared/enums/icons.enum';
import { iconMap } from 'src/app/project/shared/enums/icon-map';

@Component({
  selector: 'pp-point-fields-timeline',
  templateUrl: './point-fields-timeline.component.html',
  styleUrls: ['./point-fields-timeline.component.scss', '../point-fields.component.scss'],
  standalone: false,
})
export class PointFieldsTimelineComponent implements OnChanges, OnDestroy {
  @Input() ppWorkspaceId: string;
  @Input() ppPointId: string;
  @Input() ppFieldId: string;
  @Input() ppFieldLabel: string;
  @Input() ppNew: boolean;
  @Input() ppCanEdit: boolean;
  @Input() ppField: TPointCustomField;
  @Input() ppFieldDescription: string;
  @Input() ppIsLocked: boolean;

  private readonly destroy$ = new Subject<void>();

  value: string[] = [];

  updating = false;
  error = false;
  EIconPath = EIconPath;
  iconMap = iconMap;
  datepicker: Instance;

  constructor(
    private store: Store<{ points: TPoint[] }>,
    private promptService: PromptService,
    private pointFieldsService: PointFieldsService,
    private pointActivityService: PointActivityService,
    private sitePointFilterService: SitePointFilterService,
    private pointsUpdateService: PointsUpdateService,
    private translationPipe: TranslationPipe,
  ) {}

  ngOnChanges() {
    if (this.ppField?.value) {
      this.value = this.ppField.value.split('~');
    } else {
      this.value = [null, null];
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  setDatepicker(datepicker: Instance): void {
    this.datepicker = datepicker;
  }

  clearCustomField(): void {
    this.value = null;
    this.value = [null, null];

    this.updateField();
  }

  updateDate(dates: Date[]): void {
    if (!dates) {
      this.clearCustomField();

      return;
    }

    const [fromDate, toDate] = dates;

    this.value = [
      fromDate ? getTimeAsUtcMidday(fromDate).toString() : null,
      toDate ? getTimeAsUtcMidday(toDate).toString() : null,
    ];

    this.updateField();
  }

  updateField(): void {
    if ((this.value[0] && !this.value[1]) || (!this.value[0] && this.value[1])) {
      return;
    }

    const _id = this.ppPointId;
    const value = this.value[0] ? this.value.join('~') : null;
    const workspaceId = this.ppWorkspaceId;
    const customFieldId = this.ppFieldId;

    if (this.ppNew) {
      this.updateFieldForNewPoint(_id, value, workspaceId, customFieldId);

      return;
    }

    this.updating = true;

    const body: TPointUpdate = {
      customFieldsList: [
        {
          customFieldTemplateId: this.ppFieldId,
          value: value,
        },
      ],
    };

    this.pointsUpdateService
      .updatePointField(_id, body)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const promptText = this.translationPipe.transform('prompt_point_timeline_update');

          this.promptService.showSuccess(promptText);
          this.pointActivityService.refreshTimeline(this.ppWorkspaceId, _id);
          this.sitePointFilterService.filterPoints({ _keepScrollPosition: true });
        }),
        catchError((error) => {
          this.pointFieldsService.showUpdatePointFieldError(error);

          this.error = true;

          return of();
        }),
        finalize(() => {
          this.updating = false;
        }),
      )
      .subscribe();
  }

  private updateFieldForNewPoint(
    pointId: string,
    value: string,
    workspaceId: string,
    customFieldId: string,
  ): void {
    this.store.dispatch(
      new UpdatePointCustomField({
        workspaceId: workspaceId,
        pointId: pointId,
        customFieldId: customFieldId,
        customFieldValue: value,
      }),
    );
  }
}
